import React, { useState, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useCombined } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// TYPES
// ============================================================================

interface CombinedTxnRecord {
  id: string;
  combinedRef: string;
  closingRef: string;
  newRequestRef: string;
  customerName: string;
  settlementTotal: number;
  newLotAmount: number;
  netCashFlow: number;
  status: string;
  transactionDate: string;
}

// ============================================================================
// MAIN SCREEN
// ============================================================================

const CombinedTxnReportScreen: React.FC = () => {
  const { showMessage } = useSnackbar();

  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useCombined();
  // Data from API
  const data = (apiData as unknown as CombinedTxnRecord[]) || [];
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const searchable = `${row.combinedRef} ${row.closingRef} ${row.newRequestRef} ${row.customerName} ${row.status}`.toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      if (statusFilter && row.status !== statusFilter) return false;
      if (dateFrom) {
        const rowDate = row.transactionDate.split('T')[0];
        if (rowDate < dateFrom) return false;
      }
      if (dateTo) {
        const rowDate = row.transactionDate.split('T')[0];
        if (rowDate > dateTo) return false;
      }
      return true;
    });
  }, [data, searchQuery, statusFilter, dateFrom, dateTo]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<CombinedTxnRecord>[] = useMemo(() => [
    { id: 'combinedRef', header: 'Combined Ref', accessorKey: 'combinedRef', size: 160 },
    { id: 'closingRef', header: 'Closing Ref', accessorKey: 'closingRef', size: 150 },
    { id: 'newRequestRef', header: 'New Request Ref', accessorKey: 'newRequestRef', size: 160 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 150 },
    {
      id: 'settlementTotal',
      header: 'Settlement Total',
      accessorKey: 'settlementTotal',
      size: 160,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600, color: '#dc2626' }}>{formatCurrency(getValue())}</span>,
    },
    {
      id: 'newLotAmount',
      header: 'New Lot Amt',
      accessorKey: 'newLotAmount',
      size: 150,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600, color: '#16a34a' }}>{formatCurrency(getValue())}</span>,
    },
    {
      id: 'netCashFlow',
      header: 'Net Cash Flow',
      accessorKey: 'netCashFlow',
      size: 150,
      cell: ({ getValue }: any) => {
        const val = getValue() as number;
        return (
          <span style={{ fontWeight: 700, color: val >= 0 ? '#16a34a' : '#dc2626' }}>
            {val >= 0 ? '+' : ''}{formatCurrency(val)}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 140,
      cell: ({ getValue }: any) => <span style={getStatusBadgeStyle(getValue())}>{(getValue() ?? '').replace(/_/g, ' ')}</span>,
    },
    {
      id: 'transactionDate',
      header: 'Date',
      accessorKey: 'transactionDate',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      variant: 'icon',
      tooltip: 'Refresh',
      onClick: () => refetch(),
    },
    {
      id: 'export',
      icon: <IconSystem.download size={16} />,
      label: 'Export',
      variant: 'primary',
      tooltip: 'Export Report',
      onClick: () => showMessage('Export functionality will be available after API integration.', 'info'),
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Combined Transaction Report"
        icon={<IconSystem.copy size={20} />}
        description="Combined closing and new lot transactions with net cash flow analysis."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search transactions...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && data.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load report data</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}
        {apiLoading && data.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection={false}
              enablePagination
              enableColumnFilters={false}
              emptyMessage="No combined transactions match the current filters."
            />
          </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                  { value: 'APPROVED', label: 'Approved' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date From</label>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date To</label>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); }}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CombinedTxnReportScreen;
